// Page Me — glue between the push addon and the pager network.
//
//   addon 'push:token'   → PUT /api/pager/push (once per token per pager)
//   addon 'push:opened'  → bus 'pm:open-thread' <sender number>  (links.js navigates)
//
// Deliberately does not import the store (the store imports this). Refreshing
// the inbox on 'push:received' / 'push:opened' is the store's job.

import { bus } from '../core/state.js';
import { platform, plugin } from '../core/native.js';
import { prefs } from '../core/storage.js';
import { enablePush, pushAvailable, deviceToken } from '../../addons/push/index.js';
import { api, hasAuthToken } from './api.js';

const SENT_KEY = 'pm.pushSent';   // `${number}:${token}` last reported — not the token alone

let myNumber = null;
/** The store calls this whenever the pager's number changes (activate/restore/disconnect). */
export function setPushOwner(number) {
  myNumber = number || null;
  if (myNumber) reportToken(deviceToken());
}

async function reportToken(token) {
  if (!token || !myNumber || !hasAuthToken()) return false;
  if (platform !== 'ios' && platform !== 'android') return false;
  const key = `${myNumber}:${token.slice(-16)}`;
  if (prefs[SENT_KEY] === key) return true;
  const r = await api.put('/api/pager/push', { platform, token });
  if (r.ok) prefs[SENT_KEY] = key;
  return r.ok;
}

bus.on('push:token', (token) => { ensureChannels(); reportToken(token); });

/** The sender's number from a notification payload (APNs custom keys or FCM data). */
export function senderOf(notification) {
  const d = notification?.data ?? notification ?? {};
  const from = d.from ?? d.aps?.from ?? d['thread-id'] ?? null;
  return /^\d{10}$/.test(String(from ?? '')) ? String(from) : null;
}

/** The group a notification's page was sent to, or null for a 1:1 page. */
export function groupOf(notification) {
  const d = notification?.data ?? notification ?? {};
  const g = d.groupId ?? d.aps?.groupId ?? null;
  return /^[A-Za-z0-9_-]{6,32}$/.test(String(g ?? '')) ? String(g) : null;
}

bus.on('push:opened', (notification) => {
  const g = groupOf(notification);
  if (g) { bus.emit('pm:open-group', g); return; }
  const n = senderOf(notification);
  if (n) bus.emit('pm:open-thread', n);
});

/**
 * Ask for notifications and register this device.
 * @returns {Promise<'granted'|'denied'|'unavailable'>}
 */
// Android 8+ plays a notification's sound from its CHANNEL, fixed when the
// channel is created. The server addresses `beep_<kind>` (sound in
// res/raw/beep_<kind>.wav), `vibrate` and `silent` — see server/pageme/push.mjs.
const CHANNELS = [
  { id: 'beep_classic', name: 'Beep — classic', sound: 'beep_classic.wav' },
  { id: 'beep_chirp', name: 'Beep — chirp', sound: 'beep_chirp.wav' },
  { id: 'beep_twoway', name: 'Beep — two-way', sound: 'beep_twoway.wav' },
  { id: 'beep_ping', name: 'Beep — ping', sound: 'beep_ping.wav' },
  { id: 'vibrate', name: 'Vibrate only', vibration: true },
  { id: 'silent', name: 'Silent', importance: 2, vibration: false },
];

let channelsMade = false;
export async function ensureChannels() {
  if (channelsMade || platform !== 'android') return;
  const Push = plugin('PushNotifications');
  if (!Push?.createChannel) return;
  channelsMade = true;
  for (const c of CHANNELS) {
    try {
      await Push.createChannel({
        id: c.id,
        name: c.name,
        description: 'Pages from friends',
        importance: c.importance ?? 5,
        visibility: 1,
        vibration: c.vibration ?? true,
        ...(c.sound ? { sound: c.sound } : {}),
      });
    } catch { /* an old WebView or a denied permission — the default channel still works */ }
  }
}

export async function requestBeeps() {
  if (!pushAvailable()) return 'unavailable';
  await ensureChannels();
  let outcome;
  try { outcome = await enablePush(); } catch { outcome = 'unavailable'; }
  if (outcome === 'granted') {
    // The token usually arrives a moment after register(); the bus listener
    // above reports it. If one is already known, report it now.
    reportToken(deviceToken());
  }
  return outcome;
}

/** Forget what was reported, so the next token is sent again (disconnect). */
export function resetPushReport() {
  delete prefs[SENT_KEY];
}
